import React, { useMemo } from 'react'
import DroppableArea from '@/components/interAction/Drag/DroppableArea';
import DraggableWord from '@/components/interAction/Drag/DragableWord';
import GradingState from '@/components/organisms/Grading/GradingState';

interface GradingDiagramProps {
  activity?: any
  responseJson?: string
}

const GradingDiagram = ({ activity, responseJson }: GradingDiagramProps) => {
  const parsed = useMemo(() => {
    // 1. configJson 파싱 (카테고리와 정답 단어 목록)
    let categories: string[] = [];
    let correctWords: string[][] = [];
    
    if (activity?.configJson) {
      try {
        const config = typeof activity.configJson === 'string' 
          ? JSON.parse(activity.configJson) 
          : activity.configJson;
        
        categories = config.categories || [];
        correctWords = config.words || [];
        
        console.log('[GradingDiagram] configJson:', { categories, correctWords });
      } catch (e) {
        console.error('[GradingDiagram] configJson 파싱 오류:', e);
      }
    }
    
    // 2. responseJson 파싱 (학생의 배치 결과)
    let wordLocations: Record<string, string[]> = {};
    let completedCategories: string[] = [];
    
    if (responseJson) {
      try {
        const response = typeof responseJson === 'string' 
          ? JSON.parse(responseJson) 
          : responseJson;
        
        wordLocations = response.wordLocations || {};
        completedCategories = response.completedCategories || [];
        
        console.log('[GradingDiagram] responseJson:', { wordLocations, completedCategories });
      } catch (e) {
        console.error('[GradingDiagram] responseJson 파싱 오류:', e);
      }
    }
    
    // 3. 각 카테고리별 단어 정답 여부 계산
    const sanitizeWord = (word: string) => word?.replace(/-\d+-\d+$/, '') ?? '';

    const categoryData = categories.map((category, catIdx) => {
      const correctWordsForCategoryRaw = (correctWords[catIdx] || []);
      const correctWordsForCategory = correctWordsForCategoryRaw.map(w => w.toLowerCase());
      const studentWordsRaw = wordLocations[category] || [];
      const studentWords = studentWordsRaw.map(w => sanitizeWord(w).toLowerCase());
      
      console.log(`[GradingDiagram] ${category} - 정답:`, correctWordsForCategory, '학생답:', studentWords);
      
      // 각 단어별 정답 여부 + 색상
      const wordStatus = studentWordsRaw.map((word) => {
        const normalized = sanitizeWord(word).toLowerCase();
        const isCorrect = correctWordsForCategory.includes(normalized);
        return {
          word: sanitizeWord(word),
          isCorrect,
          color: isCorrect ? '#1D9F73' : '#F95567' // 초록/빨강
        };
      });
      
      // 카테고리 전체가 정답인지 (모든 단어가 맞아야 함)
      const allCorrect = studentWords.length > 0 && wordStatus.every(w => w.isCorrect);
      
      return {
        name: category,
        wordStatus,
        allCorrect,
        correctWords: correctWordsForCategoryRaw
      };
    });
    
    const correctSummary = categories.map((category, idx) => ({
      name: category,
      words: correctWords[idx] || []
    }));
    
    return { categoryData, correctSummary };
  }, [activity, responseJson]);

  // 카테고리별 단어 렌더링
  const renderCategoryWords = (words: Array<{ word: string; isCorrect: boolean; color: string }>) => {
    return words.map((wordItem, idx) => (
      <DraggableWord 
        key={`word-${wordItem.word}-${idx}`} 
        id={wordItem.word} 
        color={wordItem.color} 
        readOnly={true}
        className="!text-lg"
      />
    ));
  };
  
  if (!parsed.categoryData || parsed.categoryData.length === 0) {
    return (
      <div className='p-5'>
        <p className='mb-3 text-lg font-bold'>각 카테고리에 맞게 단어를 넣어주세요.</p>
        <p className='text-sm text-gray-500'>다이어그램 데이터가 없습니다.</p>
      </div>
    );
  }
  
  const hasIncorrect = parsed.categoryData.some((category) => !category.allCorrect);

  return (
    <div className=''>
        <p className='mb-3 text-lg font-bold'>각 카테고리에 맞게 단어를 넣어주세요.</p>
        <div className='grid grid-cols-2 gap-2'>
            {parsed.categoryData.map((category, index) => {
              const categoryStatus: 'correct' | 'incorrect' | 'grading' =
                category.wordStatus.length === 0
                  ? 'grading'
                  : category.allCorrect
                    ? 'correct'
                    : 'incorrect';
              return (
                <div key={`category-${category.name}-${index}`}>
                    <div className='mb-1 flex justify-between items-center gap-2 text-sm font-bold'>
                        <span>{category.name}</span>
                        <GradingState status={categoryStatus} />
                    </div>
                    <DroppableArea 
                      id={`diagram-${category.name}`} 
                      className={`${category.allCorrect ? 'border-green-500' : 'border-red-400'} border-2 rounded-lg p-2 min-h-[80px]`}
                    >
                        {renderCategoryWords(category.wordStatus)}
                    </DroppableArea>
                </div>
            )})}
        </div>
        {hasIncorrect && (
          <div className='mt-6'>
            <p className='mb-2 text-sm font-bold text-gray-600'>정답표</p>
            <div className='overflow-hidden rounded-xl border border-gray-100 bg-white'>
              <table className='min-w-full divide-y divide-gray-100 text-sm'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-4 py-2 text-left font-semibold text-gray-600'>카테고리</th>
                    <th className='px-4 py-2 text-left font-semibold text-gray-600'>정답 단어</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-50'>
                  {parsed.correctSummary.map((category) => (
                    <tr key={`answer-${category.name}`}>
                      <td className='px-4 py-2 font-medium text-gray-800'>{category.name}</td>
                      <td className='px-4 py-2 text-gray-600'>
                        {category.words.length > 0 ? category.words.join(', ') : '정답 데이터 없음'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  )
}

export default GradingDiagram